// =============================================================================
// negotiation-client.ts — Client API ADMIN de la NÉGOCIATION (réouverture ciblée), S18 / stream B, DOUBLABLE.
// =============================================================================
// Câble la SPA admin contre les surfaces back livrées par stream B (NegotiationController, AdminSessionGuard) :
//   POST /admin/campaigns/:id/negotiation/reopen   {supplierSiret, profilesAuthorized}   (garde per-profil)
//   POST /admin/campaigns/:id/negotiation/lock                                            (verrouillage final)
// + une LECTURE de l'état NegotiationAuth en cours (autorisations de réouverture ciblées) pour l'affichage.
//
// Comme les autres clients UI (campaign-analysis, publication, rank-admin…), expose une INTERFACE
// (NegotiationClient) consommée par les hooks TanStack ; l'impl HTTP réelle (fetch) vient derrière la MÊME
// interface (DI seam, stub-first), substituée sans toucher l'UI.
//
// AUTORITÉ = API/worker : la GARDE per-profil (profilesAuthorized), les transitions de participation
// (Verrouille ↔ ReouvertNego), le verrouillage final sont PORTÉS en amont et RELAYÉS ici. L'UI ne porte AUCUNE
// règle métier : elle relaie l'intention (campaignId d'URL + cible) et HABILLE. La RE-SAISIE elle-même réutilise
// la grille de saisie existante (écran fournisseur /saisie/$submissionId) sous gate négo — non ré-implémentée ici.
//
// DTO = miroirs HTTP ASCII (PAS @jeko/db) : l'enum de séniorité est répliqué en union de littéraux. Horodatages
// = ISO 8601 (string).

import { HttpApiError } from '@/api/http-error';

/** Niveau de séniorité (parité Prisma RateSeniorityLevel, ASCII — accents = i18n front). */
export type RateSeniorityLevelDto = 'Debutant' | 'Maitrisant' | 'Expert' | 'Referent';

/**
 * Un profil (couple code + niveau) AUTORISÉ à la re-saisie en négociation. L'affichage admin se BORNE à ces
 * couples (décision : « borne l'affichage aux profils autorisés ») : un fournisseur rouvert ne peut re-saisir
 * QUE les profils explicitement autorisés.
 */
export interface AuthorizedProfileDto {
  readonly profileCode: string;
  readonly seniorityLevel: RateSeniorityLevelDto;
}

/**
 * État métier 4-états d'une autorisation par-fournisseur (relayé par l'API, autorité). Miroir HTTP ASCII
 * (accents = i18n front). Invariant back : `lockedFinal === (state === 'Verrouille')`.
 *   - `ModifAutorisee`  → réouverture posée, aucune re-saisie encore effectuée (modification autorisée).
 *   - `EnCoursModif`    → re-saisie en cours (au moins un rate touché, pas encore soumis).
 *   - `Modifie`         → re-saisie effectuée et soumise (modifié, non verrouillé).
 *   - `Verrouille`      → verrouillage final relayé (négociation close pour ce fournisseur).
 */
export type NegotiationEntryStateDto =
  | 'ModifAutorisee'
  | 'EnCoursModif'
  | 'Modifie'
  | 'Verrouille';

/**
 * Une autorisation de réouverture ciblée (NegotiationAuth) active pour un fournisseur. `lockedFinal` reflète le
 * verrouillage final relayé par l'API (true ⇒ plus de re-saisie possible, négociation close pour ce fournisseur).
 */
export interface NegotiationAuthDto {
  readonly supplierSiret: string;
  /**
   * Raison sociale OPTIONNELLE (ARBITRAGE 3 S18) : le BC campaign (autorité du read /auth) ne possède QUE le
   * SIRET ; la raison sociale appartient à supplier-reference (cross-BC). Le back NE la livre PAS aujourd'hui
   * (divergence SIGNALÉE, non forcée — éviterait un ACL supplier-reference + une lecture admin cross-tenant
   * sous une RLS supplier STRICTE). L'affichage dégrade vers le SIRET quand absente.
   */
  readonly raisonSociale?: string;
  /** Profils (couples) autorisés à la re-saisie — BORNE de l'affichage admin (garde per-profil). */
  readonly profilesAuthorized: readonly AuthorizedProfileDto[];
  /** authorizedAt projeté (ISO 8601) côté back (= reopenedAt). `null` si non posé. */
  readonly reopenedAt: string | null;
  /**
   * lockedAt projeté (ISO 8601) : instant du verrouillage individuel/global de CETTE négociation
   * (`null` tant que non verrouillée). OPTIONNEL côté front (parité `raisonSociale?` : dégradation
   * tolérante si un back plus ancien ne le livre pas). Surfacé pour le JOURNAL horodaté (item 34d)
   * et la colonne de suivi. L'affichage retombe sur « — » quand absent/null.
   */
  readonly lockedAt?: string | null;
  /**
   * État métier 4-états relayé par l'API (autorité). Source de vérité de l'affichage du badge de statut.
   * `lockedFinal` en reste le miroir booléen (`lockedFinal === (state === 'Verrouille')`) — conservé pour
   * la backward-compat (gate du verrouillage, filtres existants).
   */
  readonly state: NegotiationEntryStateDto;
  /** true ⇒ verrouillage final relayé (négociation close pour ce fournisseur). Miroir de `state === 'Verrouille'`. */
  readonly lockedFinal: boolean;
}

/** Réponse de la lecture de l'état NegotiationAuth d'une campagne (liste des autorisations actives). */
export interface NegotiationAuthStateDto {
  readonly campaignId: string;
  readonly auths: readonly NegotiationAuthDto[];
}

/**
 * Un fournisseur CANDIDAT à la réouverture (cohorte de la campagne) + les profils qu'il est ÉLIGIBLE à
 * re-saisir (périmètre). L'écran BORNE la sélection (fournisseur + profils) à cette cohorte ; l'API reste
 * l'autorité (elle refuse toute cible/profil hors périmètre, garde per-profil).
 */
export interface NegotiationCandidateDto {
  readonly supplierSiret: string;
  /**
   * Raison sociale OPTIONNELLE (ARBITRAGE 3 S18) : voir NegotiationAuthDto.raisonSociale. Le read /candidates
   * (BC campaign) ne livre que le SIRET + les profils éligibles (ProfileScope figé) ; l'affichage dégrade vers
   * le SIRET quand la raison sociale est absente.
   */
  readonly raisonSociale?: string;
  readonly eligibleProfiles: readonly AuthorizedProfileDto[];
}

/** Réponse de la lecture de la cohorte candidate à la réouverture d'une campagne. */
export interface NegotiationCandidatesDto {
  readonly campaignId: string;
  readonly candidates: readonly NegotiationCandidateDto[];
}

/**
 * Corps de la RÉOUVERTURE ciblée. `supplierSiret` = fournisseur cible ; `profilesAuthorized` = couples
 * explicitement rouverts (garde per-profil). L'API refuse (4xx) une cible hors cohorte ou un profil hors
 * périmètre (autorité API) ; l'UI relaie l'intention.
 */
export interface ReopenNegotiationInput {
  readonly supplierSiret: string;
  readonly profilesAuthorized: readonly AuthorizedProfileDto[];
  /**
   * A2 — mode d'application de la liste blanche côté API (autorité). ABSENT ⇒ `'replace'` (comportement
   * historique de l'écran Négociation : la liste cochée FAIT AUTORITÉ, décocher retire l'autorisation).
   * `'add'` ⇒ l'API UNIT la liste envoyée à celle déjà posée : c'est le mode de l'ouverture PAR BINÔME
   * depuis le détail du fournisseur de la vue opérationnelle (ex-colonne « Négo. » du ranking, déplacée
   * le 2026-09-22), où « une ouverture déjà posée le reste ». L'API
   * refuse (409 NEGOTIATION_ALREADY_LOCKED) un fournisseur dont la négociation est déjà verrouillée.
   */
  readonly mode?: 'replace' | 'add';
}

/** Résultat de la réouverture ciblée (miroir HTTP) : l'autorisation créée/mise à jour. */
export interface ReopenNegotiationResultDto {
  readonly campaignId: string;
  readonly auth: NegotiationAuthDto;
}

/**
 * Résultat du verrouillage de la négociation (relayé par l'API). `lockedAt` = instant de l'opération
 * (GLOBAL : horloge ; INDIVIDUEL : lockedAt de la négo verrouillée, ou `null` si NO-OP idempotent —
 * négo absente / déjà verrouillée). L'UI relaie sans réinterpréter (autorité = API).
 */
export interface LockNegotiationResultDto {
  readonly campaignId: string;
  readonly lockedAt: string | null;
}

/** Un verrouillage EFFECTUÉ (fournisseur + instant serveur), relayé par le verrouillage par sélection. */
export interface LockedSupplierEntryDto {
  readonly supplierSiret: string;
  readonly lockedAt: string;
}

/**
 * LOT N2 — résultat du VERROUILLAGE PAR SÉLECTION. L'écran coche des LIGNES ; il envoie les fournisseurs
 * DISTINCTS de sa sélection et l'API verrouille chacun (le verrouillage reste au grain FOURNISSEUR —
 * précision GATE_HUMAN du 2026-09-18 : c'est la soumission de la grille qui bloque tous les niveaux).
 *
 * Les trois listes PARTITIONNENT la sélection dédupliquée et permettent un compte rendu honnête :
 *   • `locked`             — verrouillages réellement effectués ce tour ;
 *   • `alreadyLocked`      — déjà verrouillés ⇒ NO-OP (ce n'est PAS une erreur) ;
 *   • `withoutNegotiation` — aucune négociation ouverte ⇒ NO-OP, mais l'écran doit pouvoir le dire.
 * L'UI ne recalcule rien : elle relaie (autorité = API).
 */
export interface LockSelectionResultDto {
  readonly campaignId: string;
  readonly lockedAt: string;
  readonly locked: readonly LockedSupplierEntryDto[];
  readonly alreadyLocked: readonly string[];
  readonly withoutNegotiation: readonly string[];
}

/**
 * Une cellule COMPARÉE (profil × niveau) SNAPSHOTÉE en négociation (A-NEGO §2.5). Miroir HTTP du
 * `TjmComparisonCellDto` back (rate-collection). `seniorityLevel` est un STRING brut (le back livre le
 * littéral de niveau tel quel ; l'i18n front reste tolérante à une valeur inconnue). AUCUNE PII : montants
 * entiers, codes de profil référentiels.
 */
export interface TjmComparisonCellDto {
  readonly profileCode: string;
  readonly seniorityLevel: string;
  /** TJM figé AVANT réouverture. `null` si la cellule était Vide au snapshot. */
  readonly tjmBefore: number | null;
  /** TJM COURANT (révisé), potentiellement modifié depuis la réouverture. `null` si Vide. */
  readonly tjmAfter: number | null;
  /** tjmAfter - tjmBefore quand les deux sont renseignés ; `null` sinon (comparaison impossible). */
  readonly delta: number | null;
}

/**
 * Comparaison AVANT/APRÈS complète d'un couple (campaign, supplier) : cellules SNAPSHOTÉES uniquement
 * (celles ayant un « avant »), triées (profil, niveau) par le back, + date de modification effective. Le back
 * ne renvoie QUE les cellules snapshotées ; l'UI n'en fabrique aucune.
 */
export interface NegotiationComparisonDto {
  readonly campaignId: string;
  readonly supplierSiret: string;
  readonly cells: readonly TjmComparisonCellDto[];
  /** max(lastEditedAt) des cellules snapshotées (ISO 8601). `null` si aucune édition datée. */
  readonly dateModifEffective: string | null;
}

// =============================================================================
// LOT N1 — READ-MODEL PAR LIGNE (spec-refonte-negociation-20260917, ADR-REFONTE-NEGOCIATION-01).
// =============================================================================
// Une LIGNE = un couple Fournisseur × Profil × Séniorité (décision GATE_HUMAN « 1. OK »). Une SEULE
// requête rend toute la page : ces types REMPLACENT, à partir du lot N4, l'enchaînement
// `getAuthState` + N × `getNegotiationComparison`. Les deux surfaces cohabitent jusque-là (ajout pur).
//
// AUTORITÉ = API : l'UI ne recalcule NI l'écart, NI l'état, NI la re-saisie. Le seul dérivé laissé au
// front est le renfort d'affichage « Attente 48 h+ » (48 heures CALENDAIRES depuis `reopenedAt`,
// ADR §4.3) — un prédicat de lecture, jamais un état.

/**
 * Une LIGNE de négociation relayée par l'API. Horodatages ISO 8601 ; montants entiers en euros.
 * `state` réutilise l'énumération 4-états existante (statuts INCHANGÉS — décision GATE_HUMAN 3).
 *
 * VERROUILLAGE (précision GATE_HUMAN du 2026-09-18) : il n'existe AUCUN verrou par couple. `state`,
 * `lockedAt` et `lockedFinal` sont au grain FOURNISSEUR : toutes les lignes d'un même fournisseur les
 * portent à l'identique. Le bouton « Verrouiller » agit sur le fournisseur, le tableau affiche des lignes.
 */
export interface NegotiationLineDto {
  readonly supplierSiret: string;
  /** Raison sociale ; ABSENTE si le SIRET est inconnu côté référentiel — l'UI dégrade vers le SIRET. */
  readonly raisonSociale?: string;
  readonly profileCode: string;
  /** Libellé du profil (catalogue FIGÉ de la campagne) ; ABSENT si inconnu — dégradation vers le code. */
  readonly profileLabel?: string;
  readonly seniorityLevel: RateSeniorityLevelDto;
  /**
   * « TJM C2 » = valeur AVANT négociation, FIGÉE à l'ouverture (décision GATE_HUMAN 5). Libellé
   * FONCTIONNEL et non cyclique : sur une campagne mono-cycle, c'est la même valeur figée. `null` si
   * aucune cellule n'a été figée pour ce couple.
   */
  readonly tjmC2Eur: number | null;
  /** Valeur courante du fournisseur, renseignée UNIQUEMENT s'il y a eu une re-saisie effective. */
  readonly tjmResaisiEur: number | null;
  /** `tjmResaisiEur − tjmC2Eur`, calculé par l'API sur la valeur FIGÉE. `null` sans re-saisie. */
  readonly ecartEur: number | null;
  /** Prix Cible figé du couple. `null` si aucun prix posé. */
  readonly targetPriceEur: number | null;
  /** Ouverture de la négociation du fournisseur (ISO 8601). Base du renfort « Attente 48 h+ ». */
  readonly reopenedAt: string | null;
  /** Dernière re-saisie effective de CETTE ligne (ISO 8601). `null` sans re-saisie. */
  readonly resaisiAt: string | null;
  /** Verrouillage (grain fournisseur, ISO 8601). `null` tant que non verrouillé. */
  readonly lockedAt: string | null;
  /** État métier parmi les 4 ACTUELS. Miroir : `lockedFinal === (state === 'Verrouille')`. */
  readonly state: NegotiationEntryStateDto;
  readonly lockedFinal: boolean;
}

/** Réponse du read-model par ligne : toutes les lignes ouvertes de la campagne, déjà triées par l'API. */
export interface NegotiationLinesDto {
  readonly campaignId: string;
  readonly lines: readonly NegotiationLineDto[];
}

// --- LOT N3 — HISTORIQUE DE NÉGOCIATION -------------------------------------------------------------
// Décision GATE_HUMAN (verbatim, John 2026-09-17) : « cette liste ne doit contenir que des éléments liés
// à la négociation ». L'API ne rend QUE des événements de négociation ; l'UI n'a AUCUN filtre à appliquer
// pour exclure le reste, et ne DOIT PAS en ajouter (ce serait rendre réversible d'un clic une exclusion
// qui est une décision).
//
// L'API rend des DONNÉES, jamais des phrases : les libellés FR de la maquette (« Administrateur a
// verrouillé Datalab · FR-3.01 Expert — TJM final 410 € … ») sont composés par l'écran (lot N4) à partir
// de `type`, `actorKind`, `raisonSociale`, `lines` et des montants. Les horodatages sont SERVEUR (ISO 8601
// UTC) : le formatage FR/fuseau appartient au front.

/** Nature d'une entrée. Les onglets de la maquette sont Tous / Ouvertures / Re-saisies / Verrouillages ;
 * `Relance` n'a pas d'onglet dédié et n'apparaît que sous « Tous ». */
export type NegotiationHistoryEventTypeDto =
  | 'Ouverture'
  | 'Resaisie'
  | 'Verrouillage'
  | 'Relance';

/** Filtre d'onglet accepté par la route (`Tous` = aucun filtre). */
export type NegotiationHistoryFilterDto = 'Tous' | NegotiationHistoryEventTypeDto;

/** Nature de l'acteur. `Systeme` = relance automatique (aucun émetteur humain). */
export type NegotiationHistoryActorKindDto = 'Admin' | 'Fournisseur' | 'Systeme';

/**
 * Un couple (profil × niveau) concerné par une entrée, avec les montants des libellés chiffrés. Tous les
 * montants sont `null` sur une ouverture (le modèle n'en porte aucun à cet instant) et la liste est VIDE
 * sur une relance (l'acte porte sur le fournisseur, pas sur un couple).
 */
export interface NegotiationHistoryLineRefDto {
  readonly profileCode: string;
  /** Libellé du profil ; ABSENT si inconnu — l'UI dégrade vers le code. */
  readonly profileLabel?: string;
  readonly seniorityLevel: RateSeniorityLevelDto;
  /** « TJM C2 » = valeur AVANT négociation. */
  readonly tjmC2Eur: number | null;
  readonly tjmResaisiEur: number | null;
  /** Valeur RETENUE au verrouillage (re-saisie si elle existe, sinon TJM C2). `null` ailleurs. */
  readonly tjmFinalEur: number | null;
  readonly ecartEur: number | null;
}

/** Une entrée du flux, du plus récent au plus ancien. */
export interface NegotiationHistoryEntryDto {
  /** Clé de liste STABLE fournie par l'API (composée des faits de l'entrée) — `key` de rendu. */
  readonly entryKey: string;
  readonly type: NegotiationHistoryEventTypeDto;
  /** Horodatage SERVEUR (ISO 8601 UTC) — le formatage FR appartient à l'UI. */
  readonly occurredAt: string;
  readonly actorKind: NegotiationHistoryActorKindDto;
  /** Référence opaque de l'admin auteur ; ABSENTE pour un acte fournisseur ou système. */
  readonly actorRef?: string;
  readonly supplierSiret: string;
  /** Raison sociale ; ABSENTE si le SIRET est inconnu — l'UI dégrade vers le SIRET. */
  readonly raisonSociale?: string;
  readonly lines: readonly NegotiationHistoryLineRefDto[];
}

/** Compteurs par nature, calculés sur la TOTALITÉ du flux — étiquettes des quatre onglets. */
export interface NegotiationHistoryCountsDto {
  readonly tous: number;
  readonly ouverture: number;
  readonly resaisie: number;
  readonly verrouillage: number;
  readonly relance: number;
}

/**
 * Réponse PAGINÉE. `total` est le compteur de l'ONGLET ACTIF : c'est LUI qui alimente l'en-tête
 * « Historique · N événements », et il reste donc cohérent avec l'onglet sélectionné. `counts` alimente
 * les pastilles des quatre onglets SANS second appel.
 */
export interface NegotiationHistoryDto {
  readonly campaignId: string;
  readonly filter: NegotiationHistoryFilterDto;
  readonly entries: readonly NegotiationHistoryEntryDto[];
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
  readonly counts: NegotiationHistoryCountsDto;
}

/** Paramètres de lecture de l'historique (tous optionnels — défauts serveur : Tous, page 1, 50). */
export interface NegotiationHistoryQueryInput {
  readonly filter?: NegotiationHistoryFilterDto;
  readonly page?: number;
  readonly pageSize?: number;
}

/**
 * Erreur applicative renvoyée par l'API admin négociation (HTTP 4xx/5xx). L'UI l'affiche telle quelle ; elle ne
 * réinterprète JAMAIS la règle métier (autorité = API/worker). La garde per-profil (refus d'un profil hors
 * périmètre) remonte avec le message serveur tel quel.
 */
export class NegotiationApiError extends HttpApiError {
  public constructor(message: string, status?: number) {
    super(message, status, 'NegotiationApiError');
  }
}

/**
 * Contrat du client négociation ADMIN — miroir des surfaces back stream B (AdminSessionGuard). Aucune règle
 * métier ici : garde per-profil, transitions de participation, verrouillage final sont l'autorité de
 * l'API/worker. L'UI relaie l'intention (campaignId d'URL + cible fournisseur/profils).
 */
export interface NegotiationClient {
  /** GET cohorte candidate à la réouverture (fournisseurs + profils éligibles) — borne la sélection. */
  getCandidates(campaignId: string): Promise<NegotiationCandidatesDto>;
  /** GET état NegotiationAuth — autorisations de réouverture actives (pour l'affichage borné). */
  getAuthState(campaignId: string): Promise<NegotiationAuthStateDto>;
  /** POST .../negotiation/reopen — réouverture ciblée (garde per-profil profilesAuthorized). */
  reopen(
    campaignId: string,
    input: ReopenNegotiationInput,
  ): Promise<ReopenNegotiationResultDto>;
  /**
   * POST .../negotiation/lock — verrouillage de la négociation. `supplierSiret` ABSENT ⇒ verrouillage
   * GLOBAL (« Verrouiller la négociation », DBU l.1266) ; `supplierSiret` PRÉSENT ⇒ verrouillage
   * INDIVIDUEL de ce fournisseur (« Verrouiller ce fournisseur », DBU l.1264, item 34b). Le back porte
   * les DEUX intentions sur la même route (corps `{}` vs `{supplierSiret}`) ; l'UI relaie l'intention.
   */
  lock(
    campaignId: string,
    supplierSiret?: string,
  ): Promise<LockNegotiationResultDto>;
  /**
   * LOT N2 — POST .../negotiation/lock-selection : verrouille les fournisseurs DISTINCTS d'une sélection
   * de lignes, en UN SEUL aller-retour. L'appelant dédoublonne ou non (l'API dédoublonne de toute façon).
   * 422 si un SIRET est hors cohorte (lot refusé EN ENTIER, aucun verrouillage), 409 sur course
   * concurrente ou verrouillage non traçable, 400 si la sélection est vide / mal formée.
   */
  lockSelection(
    campaignId: string,
    supplierSirets: readonly string[],
  ): Promise<LockSelectionResultDto>;
  /**
   * GET .../negotiation/comparison?supplierSiret=<14 chiffres> — comparaison avant/après TJM d'un fournisseur
   * rouvert (cellules snapshotées uniquement). 400 si `supplierSiret` invalide (autorité API).
   */
  getNegotiationComparison(
    campaignId: string,
    supplierSiret: string,
  ): Promise<NegotiationComparisonDto>;
  /**
   * LOT N1 — GET .../negotiation/lines : TOUTES les lignes de négociation de la campagne (grain
   * Fournisseur × Profil × Séniorité) en UN SEUL aller-retour. Remplace, à partir de N4, l'enchaînement
   * `getAuthState` + N × `getNegotiationComparison` (N+2 requêtes au montage). 404 si campagne absente.
   */
  getNegotiationLines(campaignId: string): Promise<NegotiationLinesDto>;
  /**
   * LOT N3 — GET .../negotiation/history : flux des événements de NÉGOCIATION de la campagne, du plus
   * récent au plus ancien, PAGINÉ et filtrable par onglet. Le filtrage est SERVEUR (le couple
   * filtre + pagination ne peut pas être résolu à moitié côté client sans fausser « N événements »).
   * 400 si un paramètre est hors contrat, 404 si la campagne est absente.
   */
  getNegotiationHistory(
    campaignId: string,
    query?: NegotiationHistoryQueryInput,
  ): Promise<NegotiationHistoryDto>;
}
